import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    Paper,
    Box,
    Typography,
    IconButton,
    TextField,
    Avatar,
    // CircularProgress, // Not used in this version's MessageItem
    // Fade, // Removed as we are using CSS animation
    useTheme,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import './ChatBot.css'; // Import the CSS file

// --- Mock API ---
const API = {
    GetChatbotResponse: async (message: string) => {
        // Simulate thinking time
        await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 500));
        return new Promise<string>((resolve) => {
             if (message.toLowerCase().includes("status report")) {
                resolve("Generating the latest status report... Please find it attached to your dashboard soon.");
            } else if (message.toLowerCase().includes("search for invoice")) {
                resolve("Okay, please provide the invoice number or the client name associated with it.");
            } else if (message.toLowerCase() === 'hi' || message.toLowerCase() === 'hello') {
                 resolve("Greetings! I am your DDMSS Assistant. How may I help you streamline your document tasks today?");
            } else if (message.toLowerCase().includes("help")) {
                 resolve("I can help you find documents, check statuses, explain features, and more. What do you need assistance with?");
            }
             else {
                resolve(`Understood: "${message}". I'll process that request. For specific actions, try keywords like 'find', 'upload', 'status', 'report'.`);
            }
        });
    },
};

// --- Interfaces ---
interface Message {
    id: number;
    sender: 'user' | 'bot';
    text: string;
}

interface ChatBotProps {
    open: boolean;
    onClose: () => void;
}

// --- Main ChatBot Component ---
const ChatBot: React.FC<ChatBotProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isBotTyping, setIsBotTyping] = useState(false);

    // Scroll to bottom effect
    useEffect(() => {
        // No timeout needed typically when using CSS animations directly
        // If issues persist, uncomment the setTimeout:
        // const timer = setTimeout(() => {
             messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        // }, 50); // Small delay if needed
        // return () => clearTimeout(timer);
    }, [messages, isBotTyping]);

    // Load initial welcome message
    useEffect(() => {
        if (open && messages.length === 0) {
            setIsBotTyping(true);
            const loadWelcomeMessage = async () => {
                try {
                    const welcomeText = await API.GetChatbotResponse("hi");
                    setMessages([{ id: Date.now(), sender: 'bot', text: welcomeText }]);
                } catch (error) {
                    console.error("Failed to load welcome message:", error);
                    setMessages([{ id: Date.now(), sender: 'bot', text: "Sorry, I couldn't connect." }]);
                } finally {
                    setIsBotTyping(false);
                }
            };
            setTimeout(loadWelcomeMessage, 500); // Delay appearance for effect
        } else if (!open) {
             // Optional: Clear state on close if desired
             // setMessages([]);
             // setInputValue('');
             // setIsBotTyping(false);
        }
    }, [open]); // Only re-run when 'open' changes


    const handleSendMessage = async () => {
        const text = inputValue.trim();
        if (!text || isBotTyping) return;

        const userMessage: Message = { id: Date.now(), sender: 'user', text };
        setMessages(prev => [...prev, userMessage]);
        setInputValue("");
        setIsBotTyping(true);

        try {
            const botResponseText = await API.GetChatbotResponse(text);
            // Ensure unique ID for bot response
            const botResponseMessage: Message = { id: Date.now() + 1, sender: 'bot', text: botResponseText };
            setMessages(prev => [...prev, botResponseMessage]);
        } catch (error) {
             console.error("Failed to get bot response:", error);
             const botErrorMessage: Message = { id: Date.now() + 1, sender: 'bot', text: "An error occurred while responding." };
             setMessages(prev => [...prev, botErrorMessage]);
        } finally {
            setIsBotTyping(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };


    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                className: 'chatbot-ai-paper',
                sx: {
                    width: '420px',
                    height: '75vh',
                    maxHeight: '650px',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    bgcolor: '#282c34', // Example dark background
                    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header with Close Button */}
             <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
                <IconButton onClick={onClose} size="small" sx={{ color: '#a0a0a0', '&:hover': { color: '#ffffff' } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* AI Avatar Area */}
            <Box className="ai-avatar-area" sx={{ textAlign: 'center', p: 1, flexShrink: 0 }}>
                <Avatar className="ai-avatar" sx={{ width: 60, height: 60, margin: '0 auto', bgcolor: 'transparent', border: '2px solid', borderColor: theme.palette.primary.light }}>
                     <AutoAwesomeIcon sx={{ fontSize: 35, color: theme.palette.primary.light }}/>
                </Avatar>
                 <Typography variant="caption" sx={{ color: '#b0b0b0', mt: 1, display: 'block' }}>
                     DDMSS Assistant
                 </Typography>
            </Box>


            {/* Messages Area */}
            <Box
                className="messages-area"
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: 2.5,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {messages.map((msg) => (
                     // Removed Fade component here
                    <MessageItem key={msg.id} message={msg} />
                ))}
                {/* Typing Indicator */}
                 {isBotTyping && <TypingIndicator />}
                <div ref={messagesEndRef} /> {/* Scroll target */}
            </Box>

            {/* Input Area */}
            <Box
                className="input-area-ai"
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    flexShrink: 0,
                }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Ask me anything about your documents..."
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    disabled={isBotTyping}
                    sx={{
                        mr: 1.5,
                        '& .MuiOutlinedInput-root': {
                           borderRadius: '20px',
                           bgcolor: 'rgba(255, 255, 255, 0.05)',
                           color: '#e0e0e0',
                           '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                           '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                           '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                         },
                         '& .MuiInputBase-input': { color: '#f0f0f0' },
                         '& .MuiInputBase-input::placeholder': { color: '#888', opacity: 1, },
                         '& .Mui-disabled': { bgcolor: 'rgba(255, 255, 255, 0.02)' },
                         '& .Mui-disabled .MuiOutlinedInput-root': { bgcolor: 'rgba(255, 255, 255, 0.02)' }, // Ensure root bg is styled when disabled
                         '& .Mui-disabled .MuiInputBase-input': { color: '#777', WebkitTextFillColor: '#777' }, // Ensure text color when disabled
                    }}
                />
                <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isBotTyping}
                    className="send-button-ai"
                    sx={{
                         bgcolor: theme.palette.primary.main,
                         color: theme.palette.primary.contrastText,
                         '&:hover': { bgcolor: theme.palette.primary.dark },
                         '&.Mui-disabled': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: '#666' }
                    }}
                >
                    <SendIcon />
                </IconButton>
            </Box>
        </Dialog>
    );
};


// --- Message Item Component ---
const MessageItem = ({ message }: { message: Message }) => {
    const theme = useTheme();
    const isUser = message.sender === 'user';

    return (
        <Box
            // Added message-wrapper class here for CSS animation targeting
            className={`message-wrapper ${isUser ? 'user-message-wrapper' : 'bot-message-wrapper'}`}
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                alignItems: 'flex-end',
                maxWidth: '100%',
            }}
        >
            {!isUser && (
                // Spacer for alignment if needed, or a small static icon
                 <Box sx={{ width: 32, height: 32, mr: 1, flexShrink: 0 }} />
            )}
             <Paper
                elevation={isUser ? 2 : 4}
                className={`message-bubble ${isUser ? 'user-message-bubble' : 'bot-message-bubble'}`}
                sx={{
                    p: '12px 16px',
                    borderRadius: isUser ? '20px 20px 5px 20px' : '20px 20px 20px 5px',
                    bgcolor: isUser ? theme.palette.primary.dark : '#3a3f4a',
                    color: isUser ? theme.palette.primary.contrastText : '#f0f0f0',
                    maxWidth: '85%',
                    wordBreak: 'break-word',
                    position: 'relative',
                }}
            >
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    {message.text}
                </Typography>
            </Paper>
             {isUser && (
                <Avatar sx={{ bgcolor: '#666', width: 32, height: 32, ml: 1, flexShrink: 0 }}>
                    <PersonOutlineIcon fontSize="small"/>
                </Avatar>
            )}
        </Box>
    );
};

// --- Typing Indicator Component ---
const TypingIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', pl: '44px', minHeight: '40px' }}> {/* Ensure min height */}
         <Typography variant="body2" sx={{ color: '#aaa', mr: 1, fontSize: '0.8rem' }}>Assistant is typing</Typography>
        <Box className="typing-dots">
            <Box component="span" className="typing-dot" />
            <Box component="span" className="typing-dot" />
            <Box component="span" className="typing-dot" />
        </Box>
    </Box>
);


export default ChatBot;